import {
  Checkbox,
  CheckboxProps,
  Combobox,
  createDatePickerValidator,
  createForm,
  DatePicker,
  Divider,
  Flex,
  Heading,
  Notice,
  Select,
  Text,
  TextInput,
  TextInputProps,
  useToasts,
} from '@applyboard/crystal-ui'
import {
  ApplicationState,
  DocumentType,
  FileData,
} from '@applyboard/school-applications-types-lib'
import {
  COUNTRY_NAMES,
  CountryIsoCode,
  GENDER_NAMES,
  GenderCode,
  LANGUAGE_NAMES,
} from '@applyboard/school-backend-lib'
import { CreateApplicationRequest } from '@backend/agents-ui-api/routes/applications/POST/types'
import { find } from 'lodash'
import { ComponentType, useEffect, useMemo, useState } from 'react'
import { RawApplicationResponse, useCreateApplication, useGetApplicationPageProps, useUpdateApplication } from '../../../../hooks'
import { useCreateStudent } from '../../../../hooks/useCreateStudent'
import { useGetDestinationCountry } from '../../../../hooks/useGetDestinationCountry'
import {
  ConfidenceLevel,
  isPassportProcessingComplete,
  Passport,
  useGetPassport,
} from '../../../../hooks/useGetPassport'
import { useGetTimelineItems } from '../../../../hooks/useGetTimelineItems'
import { useUpdatePassport, UpdatePassportParams  } from '../../../../hooks/useUpdatePassport'
import { useUploadPassport } from '../../../../hooks/useUploadPassport'
import { useDeletePassport } from '../../../../hooks/useDeletePassport'
import { RemovePassportDialog } from '../../../../pages/CreateApplicationPage/RemovePassportDialog'
import { DestinationCountryStatus, DocumentTags, TimelineTabs } from '../../../../utils/enums'
import { transformFileInfoToUploadFileData } from '../../../../utils/transformFileInfoToUploadFileData'
import { Loading } from '../../../Loading'
import { LoadingField } from '../../../LoadingField'
import { StudentApplication, UploadFileData } from '../../types'
import { useApplicationFormContext } from '../ApplicationForm'
import { ApplicationFormCard } from '../ApplicationFormCard'
import { FileUploadField } from '../FileUploadField'
import { getFilesOfType, hasNewDocumentProcessingSchema } from '../utils'
import { ALLOWED_FILE_TYPES } from '../../../../utils/constants'
import { GenericError } from '../../../../utils'
import { Asterisk } from '../../../Asterisk'
import { UPLOAD_LIMIT, ALLOWED_PASSPORT_FILE_TYPES } from './consts'
import { copyResidentialAddress, getDefaultValue, getExtractionHelperText, hasUserModifiedExtractedPassportDetails } from './utils'
import { ExtractionFields, PersonalInformationFormFields } from './types'
import { Warnings } from './Warnings'
import { StatusSection } from './StatusSection'
import { getPassportFile } from '../../../../hooks/useGetPassportFile'
import { SpecificField } from '../../SpecificField/SpecificField'
import { Student, useGetStudent } from '../../../../hooks/useGetStudent'
import { useUpdateStudent } from '../../../../hooks/useUpdateStudent'
import { useQueryClient } from '@tanstack/react-query'
import { specificFieldValidations, getDocumentProcessingConfigPassportEnabled } from '../../../../utils/specificFieldValidations'
import { useGetApplicationPassportsHistory } from '../../../../hooks/useGetApplicationPassportsHistory'

const { Form, Field, useFieldValues, useSetFieldValues } =
  createForm<PersonalInformationFormFields>()

type PersonalInformationTabProps = {
  disabled?: boolean
  application: StudentApplication
  onSuccess: (response?: RawApplicationResponse) => void
  onError: (err: Error) => void
  currentIntakeAvailable?: boolean
  onIntakeUnavailable?: () => void
  onCreatedApplication?: (applicationId: string) => void
  isCreatingApplication?: boolean
  onNextTabButton?: () => void
}

export function PersonalInformationTab(props: Readonly<PersonalInformationTabProps>) {
  const passports = Object.values(
    props.application.attributes?.statusAndCitizenship?.passports || {},
  )

  const [passportId, setPassportId] = useState<string | undefined>()
  const [passportIds, setPassportIds] = useState<string[]>(() =>
    passports.map(p => p.passportId || ''),
  )
  const [studentId, setStudentId] = useState<string | undefined>(
    props.application.relationships?.student?.data.id
  );

  const { applicationPageProps } = useGetApplicationPageProps()
  const queryClient = useQueryClient()
  const passportProcessingTimeout = applicationPageProps?.documentProcessingConfig?.documentTypes?.PASSPORT?.timeout
  const isAiProcessingEnabled = getDocumentProcessingConfigPassportEnabled(applicationPageProps?.documentProcessingConfig)
  const [hasAiProcessingTimedOut, setHasAiProcessingTimedOut] = useState(false)

  const { resetFiles, pendingFileUploadState } = useApplicationFormContext()
  const { destinationCountryName } = useGetDestinationCountry()
  const { isCreatingApplication, createApplication } = useCreateApplication()
  const { isUpdatingApplication, updateApplication } = useUpdateApplication({
    id: props.application.id,
  })
  const { cardNumber, lastTimeline } = useGetTimelineItems(props.application, TimelineTabs.PERSONAL_INFORMATION)

  const { createStudent } = useCreateStudent();
 
  const { student, isLoadingStudent } = useGetStudent({ id: studentId })
  const { updateStudent } = useUpdateStudent()

  useEffect(() => {
    if (!studentId && !props.application.id) {
      createStudent(undefined, {
        onSuccess: (newStudent) => {
          setStudentId(newStudent.data.id);
        }
      });
    }
  }, [studentId, createStudent, !props.application.id]);

  useEffect(() => {
    setHasAiProcessingTimedOut(false)
    if (passportId && !passportIds.includes(passportId)) {
      setPassportIds([...passportIds, passportId])
    }
  }, [passportId]);

  const hasLegacyDocumentsSchema = useMemo(() => {
    const isViewMode = props.application?.attributes?.applicationState !== ApplicationState.DRAFT;
    return isViewMode && !hasNewDocumentProcessingSchema(props.application);
  }, [props.application]);

  const shouldFetchPassport = !!(
    studentId && 
    passportId && 
    !hasLegacyDocumentsSchema
  );

  const { isLoadingPassport, isFetchingPassport, passport } = useGetPassport({
    studentId,
    passportId,
    setHasAiProcessingTimedOut: setHasAiProcessingTimedOut,
    configTimeout: passportProcessingTimeout,     // This is the timeout for the passport processing coming from Document Customization Table
    enabled: shouldFetchPassport,

  });

  const { isLoadingApplicationPassportsHistory, currentPassport } = useGetApplicationPassportsHistory({
    application: props.application,
    setHasAiProcessingTimedOut,
    configTimeout: passportProcessingTimeout,
  })

  useEffect(() => {
    if (currentPassport) {
      setPassportId(currentPassport.id)
    }
  }, [currentPassport])

  const { isUpdatingPassport, updatePassport } = useUpdatePassport({
    id: passport?.id,
  })
  
  useEffect(() => {
    resetFiles(
      getFilesOfType(
        [DocumentTags.PASSPORT, DocumentTags.DESTINATION_COUNTRY_STATUS],
        props.application?.attributes?.files as FileData,
      ),
    )
  }, [props.application?.attributes?.files, resetFiles])

  const createApplicationPayload = (
    data: PersonalInformationFormFields,
    passportId: string | undefined,
    isCreate: boolean,
    pendingFileUploadState: Record<string, UploadFileData | null>,
  ): CreateApplicationRequest['data'] => {
    return {
      type: 'application',
      attributes: {
        applicationState: ApplicationState.DRAFT,
        ...(isCreate ? {
          programSelected: {
            priority: 0,
            programIntakeId: props.application?.attributes?.programSelected?.programIntakeId as string,
          },
          alternateProgramSelected: (props.application?.attributes?.alternateProgramSelected ?? []).map(program => ({
            programIntakeId: program.programIntakeId,
          })),
        } : {}),
        personalInformation: {
          basicPersonalInformation: {
            givenName: data.givenName,
            middleName: data.middleName,
            familyName: data.familyName,
            dateOfBirth: data.dateOfBirth?.substring(0, 10),
            nationality: (data.nationality as CountryIsoCode) || null,
            gender: (data.gender as GenderCode) || null,
          },
          addresses: {
            residentialAddress: {
              country: data.residentialAddressCountry as CountryIsoCode,
              addressLine1: data.residentialAddressLine1,
              addressLine2: data.residentialAddressLine2,
              city: data.residentialAddressCity,
              province: data.residentialAddressProvince,
              postalCode: data.residentialAddressPostalCode,
            },
            mailingAddress: {
              country: data.mailingAddressCountry as CountryIsoCode,
              addressLine1: data.mailingAddressLine1,
              addressLine2: data.mailingAddressLine2,
              city: data.mailingAddressCity,
              province: data.mailingAddressProvince,
              postalCode: data.mailingAddressPostalCode,
            },
          },
        },
        statusAndCitizenship: {
          destinationCountryStatus: data.immigrationStatus as DestinationCountryStatus,
          passports: passportId
            ? {
                [passportId]: {
                  passportId,
                },
              }
            : undefined,
        },
        files: pendingFileUploadState,
      },
      ...(isCreate && studentId ? {
        relationships: {
          student: {
            data: {
              id: studentId!,
              type: 'student'
            }
          }
        }
      } : {})
    };
  };

  const handleStudentUpdate = async (studentId: string | undefined, data: PersonalInformationFormFields) => {
    return studentId ? new Promise((resolve, reject) => {
      updateStudent({
        id: studentId,
        attributes: {
          personalInformation: {
            previousName: data.previousName,
            preferredName: data.preferredName,
            firstLanguageSpoken: data.firstLanguageSpoken !== '' ? data.firstLanguageSpoken : null,
          },
        },
      }, {
        onSuccess: (response) => {
          queryClient.setQueryData(['students', studentId], () => response)
          resolve(response)
        },
        onError: (error) => reject(error instanceof Error ? error : new Error(String(error))),
      })
    }) : Promise.resolve(null);
  }

  const handleSubmit = async (data: PersonalInformationFormFields) => {
    // Handle creating a new application if no application id exists.
    if (!props.application.id && props.onCreatedApplication) {
      const payload = createApplicationPayload(data, passportId, true, pendingFileUploadState)
      const { files: _, ...attributesWithoutFiles } = payload.attributes;
    
      try {
        const createApplicationPromise = new Promise<RawApplicationResponse>((resolve, reject) => {
          createApplication(
            {
              data: {
                ...payload,
                attributes: attributesWithoutFiles
              },
              files: pendingFileUploadState,
            },
            {
              onSuccess: response => {
                props.onCreatedApplication?.(response.data.id)
                resolve(response)
              },
              onError: error => reject(error instanceof Error ? error : new Error(String(error))),
            }
          );
        });
        
        // Only perform the passport update if necessary
        const updatePassportPromise = updatePassport({ passportNumber: data.passportNumber, expiryDate: data.passportExpiryDate, nationality: data.nationality })

        const updateStudentPromise = handleStudentUpdate(studentId, data);

        // Wait for both operations to succeed. If any fails, the catch block is executed.
        await Promise.all([createApplicationPromise, updatePassportPromise, updateStudentPromise]);
      } catch (error) {
        props.onError(error as GenericError)
      }

      return
    }

    // Handle disabled state or intake unavailable.
    if (props.disabled) {
      props.onSuccess()
      return
    }
    if (props.onIntakeUnavailable && !props.currentIntakeAvailable) {
      props.onIntakeUnavailable()
      return
    }

    // Handle updating an existing application.
    const payload = createApplicationPayload(data, passportId, false, pendingFileUploadState)
    const { files, ...attributesWithoutFiles } = payload.attributes
    try {
      const updateApplicationPromise = new Promise<RawApplicationResponse>((resolve, reject) => {
        updateApplication(
          { 
            attributes: attributesWithoutFiles,
            files: pendingFileUploadState,
          },
          {
            onSuccess: (response) => resolve(response),
            onError: error => reject(error instanceof Error ? error : new Error(String(error))),
          },
        );
      });
    
      // Only perform the passport update if necessary
      const updatePassportPromise = updatePassport({ passportNumber: data.passportNumber, expiryDate: data.passportExpiryDate, nationality: data.nationality })

      const updateStudentPromise = handleStudentUpdate(studentId, data);
    
      // Wait for both operations to succeed. If any fails, the catch block is executed.
      const [applicationResponse] = await Promise.all([updateApplicationPromise, updatePassportPromise, updateStudentPromise]);
    
      props.onSuccess(applicationResponse);
    } catch (error) {
      props.onError(error as GenericError);
    }
  }

  if (isLoadingStudent || isLoadingApplicationPassportsHistory) {
    return <Loading />
  }

  return (
    <Form
      defaultValues={getDefaultValue({ passport, application: props.application, hasLegacyDocumentsSchema, student })}
      onSubmit={handleSubmit}
    >
      <ApplicationFormCard
        cardNumber={cardNumber}
        totalCards={lastTimeline}
        onNextTabButton={props.onNextTabButton}
        icon="👤"
        title="Personal Information"
        isLoading={isCreatingApplication || isUpdatingApplication}
        disabled={props.disabled || (isUpdatingPassport && isAiProcessingEnabled)}
      >
        {(isAiProcessingEnabled && isLoadingPassport) || (props.isCreatingApplication && !studentId) ? (
          <Loading />
        ) : (
          <PersonalInformationTabFields
            disabled={props.disabled || isCreatingApplication || isUpdatingApplication }
            isRequestingPassportApi={isUpdatingPassport || isFetchingPassport}
            application={props.application}
            destinationCountryName={destinationCountryName}
            passport={passport}
            passportId={passportId}
            setPassportId={setPassportId}
            hasAiProcessingTimedOut={hasAiProcessingTimedOut}
            studentId={studentId || ''}
            hasLegacyDocumentsSchema={hasLegacyDocumentsSchema}
            student={student}
          />
        )}
      </ApplicationFormCard>
    </Form>
  )
}

type PersonalInformationFormFieldsProps = {
  disabled?: boolean
  application: StudentApplication
  destinationCountryName: string
  passport?: Passport
  passportId?: string
  setPassportId: (id: string) => void
  studentId: string
  isRequestingPassportApi?: boolean
  hasLegacyDocumentsSchema: boolean
  student?: Student
  hasAiProcessingTimedOut?: boolean
}

function PersonalInformationTabFields(props: Readonly<PersonalInformationFormFieldsProps>) {
  const [showUploadNotice, setShowUploadNotice] = useState(true)
  const [hasUploadedPassport, setHasUploadedPassport] = useState(false)
  const queryClient = useQueryClient()
  const toast = useToasts()
  const { applicationPageProps } = useGetApplicationPageProps()
  const isAiProcessingEnabled = getDocumentProcessingConfigPassportEnabled(applicationPageProps?.documentProcessingConfig)
  const { isSpecificFieldRequired } = specificFieldValidations(
    applicationPageProps?.specificFields,
  )
  const [showUploadErrorNotice, setShowUploadErrorNotice] = useState(false)
  const { isUploadingPassport, uploadPassport } = useUploadPassport()

  const { deletePassport } = useDeletePassport()
  const {
    immigrationFiles,
    immigrationStatus,
    isMailingAddressResidentialAddress,
    residentialAddressCountry,
    residentialAddressLine1,
    residentialAddressLine2,
    residentialAddressCity,
    residentialAddressProvince,
    residentialAddressPostalCode,
    givenName,
    familyName,
    passportNumber,
    passportExpiryDate,
    gender,
    dateOfBirth,
    nationality,
  } = useFieldValues([
    'immigrationFiles',
    'immigrationStatus',
    'isMailingAddressResidentialAddress',
    'residentialAddressCountry',
    'residentialAddressLine1',
    'residentialAddressLine2',
    'residentialAddressCity',
    'residentialAddressProvince',
    'residentialAddressPostalCode',
    'givenName',
    'familyName',
    'passportNumber',
    'passportExpiryDate',
    'gender',
    'dateOfBirth',
    'nationality',
  ])
  const setFieldValues = useSetFieldValues()
  const maxDate = new Date()
  const minDate = new Date(1900, 0, 1)
  maxDate.setFullYear(maxDate.getFullYear() + 10)
  minDate.setFullYear(minDate.getFullYear() - 3)
  

const [openRemovePassportDialog, setOpenRemovePassportDialog] = useState(false)

  // When viewing applications with legacy document schema, retrieve legacy passport section reference
  // so that we can point to the correct section in the file upload field
  const legacyPassportId = find(props.application?.attributes?.files, itm => (itm?.type as unknown as DocumentTags | undefined) === DocumentTags.PASSPORT && !!itm?.activeRecord)?.sectionReference;

  const datePickerValidator = createDatePickerValidator(minDate, maxDate, 'day')


  const isExtractingData = (!props.hasLegacyDocumentsSchema && !isPassportProcessingComplete(
    props.passport,
    props.hasAiProcessingTimedOut
  ))

  useEffect(() => {
    if (!isAiProcessingEnabled && props.passport?.attributes.expiryDate && props.passport?.attributes.passportNumber) {
        setFieldValues({
          passportNumber: props.passport?.attributes.passportNumber,
          passportExpiryDate: new Date(props.passport?.attributes.expiryDate).toISOString(),
        })
    }
  }, [props.passport?.attributes.expiryDate, props.passport?.attributes.passportNumber, isAiProcessingEnabled, setFieldValues])
  // Reset upload notice visibility when a new passport is uploaded
  useEffect(() => {
    if (props.passportId) {
      setShowUploadNotice(true)
    }
  }, [props.passportId])

  
// Only auto-populate fields when AI processing is enabled AND processing is complete
useEffect(() => {
  if(!isExtractingData && isAiProcessingEnabled) {
    const defaultValues = getDefaultValue({
      application: props.application,
      passport: props.passport,
      hasLegacyDocumentsSchema: props.hasLegacyDocumentsSchema,
      student: props.student,
    })

    let address = {}

    if (isMailingAddressResidentialAddress) {
      address = copyResidentialAddress({
        residentialAddressCountry: defaultValues.residentialAddressCountry,
        residentialAddressLine1: defaultValues.residentialAddressLine1,
        residentialAddressLine2: defaultValues.residentialAddressLine2,
        residentialAddressCity: defaultValues.residentialAddressCity,
        residentialAddressProvince: defaultValues.residentialAddressProvince,
        residentialAddressPostalCode: defaultValues.residentialAddressPostalCode,
      })
    }

    setFieldValues({
      ...defaultValues,
      ...address,
    })
  }
}, [props.passport])


  const getExtractionHelperTextWrapper = useMemo(() => {
    return (extractedData?: { value?: string, confidence?: ConfidenceLevel }, currentValue?: string, field?: ExtractionFields) => {
      if (props.hasLegacyDocumentsSchema) return {}
      
      return getExtractionHelperText({
        applicationPassportId: Object.values(
          props.application.attributes?.statusAndCitizenship?.passports || {},
        )[0]?.passportId,
        passportId: props.passport?.id,
        extractedData,
        currentValue,
        field
      });
    };
  }, [props.hasLegacyDocumentsSchema, props.application, props.passport]);

  if (!props.hasLegacyDocumentsSchema && !props.studentId) {
    return <Loading />
  }
  

  return (
    <>
      {isAiProcessingEnabled ? (
      <Flex gap={4} direction={{ xs: 'column', sm: 'row' }} wrap pt={4} pb={4}>
        <Flex basis="100%">
          <Heading variant="titleS" level={3}>
            ✈️ Step 1: Provide passport
          </Heading>
        </Flex>
          {(props.passport && isExtractingData && isAiProcessingEnabled) ||
          (isUploadingPassport && isAiProcessingEnabled) ? (
          <Flex gap={4} direction="column" basis="100%">
            <Notice intent="primary">
              Processing document... This might take a few minutes.
            </Notice>
          </Flex>
        ) : null}
        {!props.disabled &&
        props.passport &&
        props.passport.id !==
          Object.values(props.application.attributes?.statusAndCitizenship?.passports || {})[0]
            ?.passportId &&
        showUploadNotice ? (
          <Flex gap={4} direction="column" basis="100%">
            <Warnings
              documentProcessStatus={props.passport.attributes.documentProcessStatus}
              documentTypeConfidence={
                props.passport.attributes.processingResults?.documentTypeConfidence
              }
              hasAiProcessingTimedOut={props.hasAiProcessingTimedOut}
              onClick={() => setShowUploadNotice(false)}
            />
          </Flex>
        ) : null}

        {showUploadErrorNotice ? (
          <Flex gap={4} direction="column" basis="100%">
            <Notice intent="negative">
              Upload failed. Please try again. If the problem persists, check your file format and
              size.
              <Notice.CloseButton onClick={() => setShowUploadErrorNotice(false)} />
            </Notice>
          </Flex>
        ) : null}
        <Field
          as={FileUploadField}
          allowedFileTypes={ALLOWED_FILE_TYPES}
          application={props.application}
          disabled={props.disabled || isUploadingPassport}
          fileLimit={UPLOAD_LIMIT}
          fileType={DocumentTags.PASSPORT}
          label={
            props.passport || isUploadingPassport ? (
              ''
            ) : (
              <Text variant={'bodyM'}>
                A passport is required to proceed with the application. We'll use it to:
                <ul>
                  <li>Verify the student's identity</li>
                  <li>Pre-fill application fields (e.g., name, date of birth, gender)</li>
                </ul>
                  Add a passport document below, supported file formats: JPG, JPEG, PDF, PNG, max
                  number of files: {UPLOAD_LIMIT} <Asterisk />
              </Text>
            )
          }
          name={`passportFiles`}
          onRemove={() => {
            setOpenRemovePassportDialog(true)
          }}
            section={
              props.hasLegacyDocumentsSchema ? `${legacyPassportId}` : `${props.passport?.id}`
            }
          showHistory={!!props.disabled}
          validate={() => {
            if (!props.passport?.id) {
              return 'This field is required'
            }

            return true
          }}
          hideUploadComponent={Boolean(props.passport || isUploadingPassport)}
          showLoading={isUploadingPassport}
          multiple={false}
          presetValue={
            props.passport
              ? [
                  {
                    id: props.passport.id,
                    uploadDate: new Date(props.passport.attributes.uploadedAt),
                    tags: [DocumentTags.PASSPORT],
                    name: props.passport.attributes.fileName,
                    section: '',
                    history: [],
                    isBlankDatePlaceholder: () => true,
                    onClick: async () => {
                      if (props.passport) {
                        const req = await getPassportFile(props.studentId, props.passport.id)
                          window.open(
                            req.data.meta.downloadUrl,
                            `passport_${props.passport.id}_${Date.now()}`,
                          )
                      }
                    },
                  },
                ]
              : undefined
          }
          onChange={([passportFile]) => {
            if (passportFile) {
              setShowUploadErrorNotice(false)

              if (
                !ALLOWED_FILE_TYPES.find(fileExtension =>
                  passportFile.file.name.toLowerCase().endsWith(fileExtension),
                )
              ) {
                passportFile.errorMessage = `Incorrect file format. Only the following file types may be uploaded: ${ALLOWED_FILE_TYPES.join(
                  ', ',
                )}`

                return false
              }

                setHasUploadedPassport(true)
              uploadPassport(
                {
                  studentId: props.studentId,
                  passportDocument: transformFileInfoToUploadFileData(passportFile, {
                    sectionReference: `${props.passport?.id}`,
                    type: DocumentTags.PASSPORT as unknown as DocumentType,
                  }),
                },
                {
                  onSuccess: response => {
                    props.setPassportId(response.data.id)
                  },
                  onError: err => {
                    passportFile.errorMessage =
                      'Error while trying to upload the file. Please try again.'
                    setFieldValues({ passportFiles: [passportFile] })
                    setShowUploadErrorNotice(true)
                    toast.negative(err)
                  },
                },
              )
            }
          }}
        />
      </Flex>
      ) : null}

      {/*
        This scenario occurs only in Draft mode when no personal data has been saved.
        Once the personal data is saved, the file cannot be removed by clicking the "X"
        but can only be replaced using the "Replace" button.
      */}
      {openRemovePassportDialog && (
        <RemovePassportDialog
          open={openRemovePassportDialog}
          onOpenChange={setOpenRemovePassportDialog}
          onConfirm={() => {
            setFieldValues({
              passportFiles: [],
              passportNumber: '',
              passportExpiryDate: '',
              familyName: '',
              givenName: '',
              nationality: '',
              dateOfBirth: '',
              gender: '',
            })
            setOpenRemovePassportDialog(false)
            setHasUploadedPassport(false)
            props.setPassportId('')
          }}
          passportId={props.passport?.id ?? ''}
          applicationId={props.application.id}
          studentId={props.studentId}
        />
      )}

      {props.passport || props.hasLegacyDocumentsSchema || !isAiProcessingEnabled ? (
        <>
          <Flex gap={4} direction={{ xs: 'column', sm: 'row' }} wrap pt={4} pb={4}>
            <Flex.Item basis={{ xs: '100%', sm: 'calc(50% - 8px)' }}>
              {isExtractingData && isAiProcessingEnabled ? (
                <LoadingField label="Passport number" required />
              ) : (
                <Field
                  as={TextInput as ComponentType<TextInputProps>}
                  label="Passport number"
                  name="passportNumber"
                  disabled={props.disabled || props.isRequestingPassportApi}
                  required={!props.disabled ? 'Passport number is required' : false}
                  {...getExtractionHelperTextWrapper(
                    props.passport?.attributes.processingResults?.extractedData?.passportNumber,
                    passportNumber,
                  )}
                />
              )}
            </Flex.Item>
            <Flex.Item basis={{ xs: '100%', sm: 'calc(50% - 8px)' }}>
              {isExtractingData && isAiProcessingEnabled ? (
                <LoadingField label="Passport expiry date" required />
              ) : (
                <Field
                  as={DatePicker}
                  label="Passport expiry date"
                  name="passportExpiryDate"
                  maxDate={maxDate.toISOString()}
                  minDate={minDate.toISOString()}
                  disabled={props.disabled || props.isRequestingPassportApi}
                  required={!props.disabled ? 'Passport expiry date is required' : false}
                  {...getExtractionHelperTextWrapper(
                    props.passport?.attributes.processingResults?.extractedData?.expiryDate,
                    passportExpiryDate,
                  )}
                />
              )}
            </Flex.Item>

            <Flex.Item basis={{ xs: '100%', sm: 'calc(50% - 8px)' }}>
              {isExtractingData && isAiProcessingEnabled ? (
                <LoadingField label="Given name" required />
              ) : (
                <Field
                  as={TextInput as ComponentType<TextInputProps>}
                  label="Given name"
                  name="givenName"
                  disabled={props.disabled || props.isRequestingPassportApi}
                  required={!props.disabled ? 'Given name is required' : false}
                  {...getExtractionHelperTextWrapper(
                    props.passport?.attributes.processingResults?.extractedData?.firstName,
                    givenName,
                    'firstName',
                  )}
                />
              )}
            </Flex.Item>
            <Flex.Item basis={{ xs: '100%', sm: 'calc(50% - 8px)' }}>
              {isExtractingData && isAiProcessingEnabled ? (
                <LoadingField label="Family name (if applicable)" />
              ) : (
                <Field
                  as={TextInput as ComponentType<TextInputProps>}
                  label="Family name (if applicable)"
                  name="familyName"
                  disabled={props.disabled || props.isRequestingPassportApi}
                  {...getExtractionHelperTextWrapper(
                    props.passport?.attributes.processingResults?.extractedData?.familyName,
                    familyName,
                    'familyName',
                  )}
                />
              )}
            </Flex.Item>
            <Flex.Item basis={{ xs: '100%', sm: 'calc(50% - 8px)' }}>
              {isExtractingData && isAiProcessingEnabled ? (
                <LoadingField label="Middle name" />
              ) : (
                <Field
                  as={TextInput as ComponentType<TextInputProps>}
                  label="Middle name"
                  name="middleName"
                  disabled={props.disabled || props.isRequestingPassportApi}
                />
              )}
            </Flex.Item>
            <SpecificField
              field="personalInformation.previousName"
              forceEnabled={!!props.student?.attributes?.personalInformation?.previousName}
            >
              <Flex.Item basis={{ xs: '100%', sm: 'calc(50% - 8px)' }}>
                {isExtractingData && isAiProcessingEnabled ? (
                  <LoadingField label="Previous name" />
                ) : (
                  <Field
                    as={TextInput as ComponentType<TextInputProps>}
                    label="Previous name"
                    name="previousName"
                    helpText="Full previous legal name, if applicable."
                    disabled={props.disabled || props.isRequestingPassportApi}
                    required={
                      !props.disabled && isSpecificFieldRequired('personalInformation.previousName')
                        ? 'Previous name is required'
                        : false
                    }
                  />
                )}
              </Flex.Item>
            </SpecificField>
            <SpecificField
              field="personalInformation.preferredName"
              forceEnabled={!!props.student?.attributes?.personalInformation?.preferredName}
            >
              <Flex.Item basis={{ xs: '100%', sm: 'calc(50% - 8px)' }}>
                {isExtractingData && isAiProcessingEnabled ? (
                  <LoadingField label="Preferred name" />
                ) : (
                  <Field
                    as={TextInput as ComponentType<TextInputProps>}
                    label="Preferred name"
                    name="preferredName"
                    disabled={props.disabled || props.isRequestingPassportApi}
                    required={
                      !props.disabled &&
                      isSpecificFieldRequired('personalInformation.preferredName')
                        ? 'Preferred name is required'
                        : false
                    }
                  />
                )}
              </Flex.Item>
            </SpecificField>
            <Flex.Item basis={{ xs: '100%', sm: 'calc(50% - 8px)' }}>
              {isExtractingData && isAiProcessingEnabled ? (
                <LoadingField label="Gender" required />
              ) : (
                <Field
                  as={Select}
                  label="Gender"
                  name="gender"
                  appearance="styled"
                  disabled={props.disabled || props.isRequestingPassportApi}
                  required={!props.disabled ? 'Gender is required' : false}
                  {...getExtractionHelperTextWrapper(
                    props.passport?.attributes.processingResults?.extractedData?.sex,
                    gender,
                  )}
                >
                  {Object.entries(GENDER_NAMES).map(([genderCode, genderName]) => (
                    <Select.Option key={genderCode} label={genderName} value={genderCode} />
                  ))}
                </Field>
              )}
            </Flex.Item>
            <Flex.Item basis={{ xs: '100%', sm: 'calc(50% - 8px)' }}>
              {isExtractingData && isAiProcessingEnabled ? (
                <LoadingField label="Date of birth" required />
              ) : (
                <Field
                  as={DatePicker}
                  label="Date of birth"
                  name="dateOfBirth"
                  maxDate={maxDate.toISOString()}
                  minDate={minDate.toISOString()}
                  disabled={props.disabled || props.isRequestingPassportApi}
                  required={!props.disabled ? 'Date of birth is required' : false}
                  validate={datePickerValidator}
                  {...getExtractionHelperTextWrapper(
                    props.passport?.attributes.processingResults?.extractedData?.birthDate,
                    dateOfBirth,
                  )}
                />
              )}
            </Flex.Item>
            <Flex.Item basis={{ xs: '100%', sm: 'calc(50% - 8px)' }}>
              {isExtractingData && isAiProcessingEnabled ? (
                <LoadingField label="Nationality" required />
              ) : (
                <Field
                  as={Combobox}
                  label="Nationality"
                  name="nationality"
                  size="md"
                  placeholder="Select"
                  disabled={props.disabled || props.isRequestingPassportApi}
                  required={!props.disabled ? 'Nationality is required' : false}
                  {...getExtractionHelperTextWrapper(
                    props.passport?.attributes.processingResults?.extractedData?.nationality,
                    nationality,
                  )}
                >
                  {Object.entries(COUNTRY_NAMES).map(([countryCode, countryName]) => (
                    <Combobox.Option key={countryCode} label={countryName} value={countryCode} />
                  ))}
                </Field>
              )}
            </Flex.Item>
            <SpecificField
              field="personalInformation.firstLanguageSpoken"
              forceEnabled={!!props.student?.attributes?.personalInformation?.firstLanguageSpoken}
            >
              <Flex.Item basis={{ xs: '100%', sm: 'calc(50% - 8px)' }}>
                {isExtractingData && isAiProcessingEnabled ? (
                  <LoadingField label="First language spoken" />
                ) : (
                  <Field
                    as={Combobox}
                    label="First language spoken"
                    name="firstLanguageSpoken"
                    size="md"
                    placeholder="Select"
                    type="search"
                    disabled={props.disabled || props.isRequestingPassportApi}
                    required={
                      !props.disabled &&
                      isSpecificFieldRequired('personalInformation.firstLanguageSpoken')
                        ? 'First language spoken is required'
                        : false
                    }
                  >
                    {Object.entries(LANGUAGE_NAMES).map(([languageCode, languageName]) => (
                      <Combobox.Option
                        key={languageCode}
                        label={languageName}
                        value={languageCode}
                      />
                    ))}
                  </Field>
                )}
              </Flex.Item>
            </SpecificField>
          </Flex>

          {!isAiProcessingEnabled ? (
            <>
              <Divider />
              <Flex gap={4} direction={{ xs: 'column', sm: 'row' }} wrap pt={4} pb={4}>
                {showUploadErrorNotice ? (
                  <Flex gap={4} direction="column" basis="100%">
                    <Notice intent="negative">
                      Upload failed. Please try again. If the problem persists, check your file
                      format and size.
                      <Notice.CloseButton onClick={() => setShowUploadErrorNotice(false)} />
                    </Notice>
                  </Flex>
                ) : null}
                <Field
                  as={FileUploadField}
                  allowedFileTypes={ALLOWED_PASSPORT_FILE_TYPES}
                  application={props.application}
                  disabled={props.disabled || isUploadingPassport}
                  fileLimit={UPLOAD_LIMIT}
                  fileType={DocumentTags.PASSPORT}
                  label={
                    props.passport || isUploadingPassport || props.passportId ? (
                      ''
                    ) : (
                      <Flex direction="column" gap={3}>
                        <Heading level={2} variant="titleS">
                          Upload your passport
                        </Heading>
                        <Text variant="bodyM">
                          Please provide a clear and legible copy of your passport.
                        </Text>
                      </Flex>
                    )
                  }
                  name={`passportFiles`}
                  onRemove={() => {
                    deletePassport(
                      {
                        studentId: props.studentId,
                        passportId: props.passport?.id || '',
                        applicationId: props.application.id,
                      },
                      {
                        onSuccess: () => {
                          queryClient.invalidateQueries({
                            queryKey: ['applications', props.application.id],
                          })
                          queryClient.removeQueries({
                            queryKey: ['passport', props.studentId, props.passport?.id],
                          })
                          queryClient.removeQueries({
                            queryKey: ['applicationPassports', props.application.id],
                          })
                          setFieldValues({
                            passportFiles: [],
                          })
                          props.setPassportId('')
                          toast.positive('The passport has been successfully removed.')
                        },
                        onError: err => {
                          if (err instanceof Error) {
                            toast.negative(new Error(err.message))
                          }
                        },
                      },
                    )
                  }}
                  section={`${props.passport?.id}`}
                  showHistory={!!props.disabled}
                  validate={(value): string | true => {
                    // If there's a passport uploaded (via ID), validation passes regardless of form field
                    if (props.passportId || props.passport?.id) {
                      return true
                    }
                    // If no passport uploaded, check if form field has files
                    if (!value || value.length === 0) {
                      return 'Passport is required'
                    }
                    return true
                  }}
                  hideUploadComponent={Boolean(
                    props.passport || props.passportId || isUploadingPassport || hasUploadedPassport,
                  )}
                  showLoading={isUploadingPassport || (!!props.passportId && !props.passport) || (hasUploadedPassport && !props.passport)}
                  multiple={false}
                  presetValue={
                    props.passport
                      ? [
                          {
                            id: props.passport.id,
                            uploadDate: new Date(props.passport.attributes.uploadedAt),
                            tags: [DocumentTags.PASSPORT],
                            name: props.passport.attributes.fileName,
                            section: '',
                            history: [],
                            isBlankDatePlaceholder: () => true,
                            onClick: async () => {
                              if (props.passport) {
                                const req = await getPassportFile(props.passport.id)
                                window.open(
                                  req.data.meta.downloadUrl,
                                  `passport_${props.passport.id}_${Date.now()}`,
                                )
                              }
                            },
                            onError: (error: Error) => {
                              toast.negative(error)
                            },
                          },
                        ]
                      : undefined
                  }
                  onChange={([passportFile]) => {
                    if (passportFile) {
                      setShowUploadErrorNotice(false)

                      if (
                        !ALLOWED_PASSPORT_FILE_TYPES.find(fileExtension =>
                          passportFile.file.name.toLowerCase().endsWith(fileExtension),
                        )
                      ) {
                        passportFile.errorMessage = `Incorrect file format. Only the following file types may be uploaded: ${ALLOWED_PASSPORT_FILE_TYPES.join(
                          ', ',
                        )}`

                        return false
                      }

                      uploadPassport(
                        {
                          studentId: props.studentId,
                          passportDocument: transformFileInfoToUploadFileData(passportFile, {
                            sectionReference: `${props.passport?.id}`,
                            type: DocumentTags.PASSPORT as unknown as DocumentType,
                          }),
                        },
                        {
                          onSuccess: async response => {
                            await queryClient.setQueryData(
                              ['passport', response.data.id],
                              response.data,
                            )
                            props.setPassportId(response.data.id)
                          },
                          onError: err => {
                            setShowUploadErrorNotice(true)
                            passportFile.errorMessage = `Error while trying to upload the file. Please try again.`
                            setFieldValues({ passportFiles: [passportFile] })
                            toast.negative(err)
                          },
                        },
                      )
                    }
                  }}
                />
                {(!props.passport?.id && !props.passportId && !isUploadingPassport && !hasUploadedPassport) && (
                  <Text variant="bodyS" contrast="mid">
                    Supported file formats: JPG, JPEG, PDF, PNG, max number of files: {UPLOAD_LIMIT}{' '}
                    <Asterisk />
                  </Text>
                )}
              </Flex>
            </>
          ) : null}

          {!isExtractingData || !isAiProcessingEnabled ? (
            <>
              <Divider />

              <Flex gap={4} direction={{ xs: 'column', sm: 'row' }} wrap pt={4} pb={4}>
                <Flex basis="100%">
                  <Heading variant="titleS" level={3}>
                    ✈️ Step 2: Provide additional information
                  </Heading>
                </Flex>
                <Flex basis="100%">
                  <Heading variant="titleS" level={3}>
                    <span aria-hidden>🏢</span> Residential Address
                  </Heading>
                </Flex>
                <Flex.Item basis={{ xs: '100%', sm: 'calc(50% - 8px)' }}>
                  {isExtractingData && isAiProcessingEnabled ? (
                    <LoadingField label="Country" required />
                  ) : (
                    <Field
                      as={Combobox}
                      label="Country"
                      name="residentialAddressCountry"
                      size="md"
                      placeholder="Select"
                      disabled={props.disabled || props.isRequestingPassportApi}
                      required={!props.disabled ? 'Country is required' : false}
                      onChange={value => {
                        if (isMailingAddressResidentialAddress) {
                          setFieldValues({
                            ...copyResidentialAddress({
                              residentialAddressCountry,
                              residentialAddressLine1,
                              residentialAddressLine2,
                              residentialAddressCity,
                              residentialAddressProvince,
                              residentialAddressPostalCode,
                            }),
                            mailingAddressCountry: value as string,
                          })
                        }
                      }}
                    >
                      {Object.entries(COUNTRY_NAMES).map(([countryCode, countryName]) => (
                        <Combobox.Option
                          key={countryCode}
                          label={countryName}
                          value={countryCode}
                        />
                      ))}
                    </Field>
                  )}
                </Flex.Item>
                <Flex pb={6} gap={4} direction={{ xs: 'column', sm: 'row' }} wrap>
                  <Flex.Item basis={{ xs: '100%' }}>
                    {isExtractingData && isAiProcessingEnabled ? (
                      <LoadingField label="Address line 1" required />
                    ) : (
                      <Field
                        as={TextInput as ComponentType<TextInputProps>}
                        label="Address line 1"
                        name="residentialAddressLine1"
                        disabled={props.disabled || props.isRequestingPassportApi}
                        required={!props.disabled ? 'Address is required' : false}
                        helpText="E.g. Street address"
                        onChange={value => {
                          if (isMailingAddressResidentialAddress) {
                            setFieldValues({
                              ...copyResidentialAddress({
                                residentialAddressCountry,
                                residentialAddressLine1,
                                residentialAddressLine2,
                                residentialAddressCity,
                                residentialAddressProvince,
                                residentialAddressPostalCode,
                              }),
                              mailingAddressLine1: value as string,
                            })
                          }
                        }}
                      />
                    )}
                  </Flex.Item>
                  <Flex.Item basis={{ xs: '100%' }}>
                    {isExtractingData && isAiProcessingEnabled ? (
                      <LoadingField label="Address line 2" />
                    ) : (
                      <Field
                        as={TextInput as ComponentType<TextInputProps>}
                        label="Address line 2"
                        name="residentialAddressLine2"
                        disabled={props.disabled || props.isRequestingPassportApi}
                        helpText="E.g. Apartment, suite, unit"
                        onChange={value => {
                          if (isMailingAddressResidentialAddress) {
                            setFieldValues({
                              ...copyResidentialAddress({
                                residentialAddressCountry,
                                residentialAddressLine1,
                                residentialAddressLine2,
                                residentialAddressCity,
                                residentialAddressProvince,
                                residentialAddressPostalCode,
                              }),
                              mailingAddressLine2: value as string,
                            })
                          }
                        }}
                      />
                    )}
                  </Flex.Item>
                  <Flex basis="100%" gap={4}>
                    <Flex.Item basis={{ xs: '100%', sm: 'calc(50% - 8px)' }}>
                      {isExtractingData && isAiProcessingEnabled ? (
                        <LoadingField label="Province/state/region" required />
                      ) : (
                        <Field
                          as={TextInput as ComponentType<TextInputProps>}
                          label="Province/state/region"
                          name="residentialAddressProvince"
                          disabled={props.disabled || props.isRequestingPassportApi}
                          required={!props.disabled ? 'Province/state/region is required' : false}
                          onChange={value => {
                            if (isMailingAddressResidentialAddress) {
                              setFieldValues({
                                ...copyResidentialAddress({
                                  residentialAddressCountry,
                                  residentialAddressLine1,
                                  residentialAddressLine2,
                                  residentialAddressCity,
                                  residentialAddressProvince,
                                  residentialAddressPostalCode,
                                }),
                                mailingAddressProvince: value as string,
                              })
                            }
                          }}
                        />
                      )}
                    </Flex.Item>
                    <Flex.Item basis={{ xs: '100%', sm: 'calc(50% - 8px)' }}>
                      {isExtractingData && isAiProcessingEnabled ? (
                        <LoadingField label="City/town" required />
                      ) : (
                        <Field
                          as={TextInput as ComponentType<TextInputProps>}
                          label="City/town"
                          name="residentialAddressCity"
                          disabled={props.disabled || props.isRequestingPassportApi}
                          required={!props.disabled ? 'City/town is required' : false}
                          onChange={value => {
                            if (isMailingAddressResidentialAddress) {
                              setFieldValues({
                                ...copyResidentialAddress({
                                  residentialAddressCountry,
                                  residentialAddressLine1,
                                  residentialAddressLine2,
                                  residentialAddressCity,
                                  residentialAddressProvince,
                                  residentialAddressPostalCode,
                                }),
                                mailingAddressCity: value as string,
                              })
                            }
                          }}
                        />
                      )}
                    </Flex.Item>
                  </Flex>
                  <Flex.Item basis={{ xs: '100%', sm: 'calc(50% - 8px)' }}>
                    {isExtractingData && isAiProcessingEnabled ? (
                      <LoadingField label="Postal/zip code" />
                    ) : (
                      <Field
                        as={TextInput as ComponentType<TextInputProps>}
                        label="Postal/zip code"
                        name="residentialAddressPostalCode"
                        disabled={props.disabled || props.isRequestingPassportApi}
                        onChange={value => {
                          if (isMailingAddressResidentialAddress) {
                            setFieldValues({
                              ...copyResidentialAddress({
                                residentialAddressCountry,
                                residentialAddressLine1,
                                residentialAddressLine2,
                                residentialAddressCity,
                                residentialAddressProvince,
                                residentialAddressPostalCode,
                              }),
                              mailingAddressPostalCode: value as string,
                            })
                          }
                        }}
                      />
                    )}
                  </Flex.Item>
                </Flex>

                <Flex direction={'column'}>
                  <Flex basis={'100%'}>
                    <Heading variant="titleS" level={3}>
                      <span aria-hidden>✉️</span> Full Mailing Address
                    </Heading>
                  </Flex>

                  <Flex basis={'100%'}>
                    {isExtractingData && isAiProcessingEnabled ? (
                      <LoadingField label="Mailing address is the same as residential address" />
                    ) : (
                      <Field
                        as={Checkbox as ComponentType<CheckboxProps>}
                        name="isMailingAddressResidentialAddress"
                        label="Mailing address is the same as residential address"
                        disabled={props.disabled || props.isRequestingPassportApi}
                        onChange={value => {
                          if (!value) {
                            setFieldValues({
                              mailingAddressCountry: '',
                              mailingAddressLine1: '',
                              mailingAddressLine2: '',
                              mailingAddressCity: '',
                              mailingAddressProvince: '',
                              mailingAddressPostalCode: '',
                            })
                          } else {
                            setFieldValues(
                              copyResidentialAddress({
                                residentialAddressCountry,
                                residentialAddressLine1,
                                residentialAddressLine2,
                                residentialAddressCity,
                                residentialAddressProvince,
                                residentialAddressPostalCode,
                              }),
                            )
                          }
                        }}
                      />
                    )}
                  </Flex>

                  {!isMailingAddressResidentialAddress ? (
                    <>
                      <Flex basis={'100%'} pb={4}>
                        <Flex.Item basis={{ xs: '100%', sm: 'calc(50% - 8px)' }}>
                          {isExtractingData && isAiProcessingEnabled ? (
                            <LoadingField label="Country" required />
                          ) : (
                            <Field
                              as={Combobox}
                              label="Country"
                              name="mailingAddressCountry"
                              size="md"
                              placeholder="Select"
                              disabled={props.disabled || props.isRequestingPassportApi}
                              required={!props.disabled ? 'Country is required' : false}
                            >
                              {Object.entries(COUNTRY_NAMES).map(([countryCode, countryName]) => (
                                <Combobox.Option
                                  key={countryCode}
                                  label={countryName}
                                  value={countryCode}
                                />
                              ))}
                            </Field>
                          )}
                        </Flex.Item>
                      </Flex>

                      <Flex gap={4} direction={{ xs: 'column', sm: 'row' }} wrap>
                        <Flex.Item basis={{ xs: '100%' }}>
                          {isExtractingData && isAiProcessingEnabled ? (
                            <LoadingField label="Address line 1" required />
                          ) : (
                            <Field
                              as={TextInput as ComponentType<TextInputProps>}
                              label="Address line 1"
                              name="mailingAddressLine1"
                              disabled={props.disabled || props.isRequestingPassportApi}
                              required={!props.disabled ? 'Address is required' : false}
                              helpText="E.g. Street address"
                            />
                          )}
                        </Flex.Item>
                        <Flex.Item basis={{ xs: '100%' }}>
                          {isExtractingData && isAiProcessingEnabled ? (
                            <LoadingField label="Address line 2" />
                          ) : (
                            <Field
                              as={TextInput as ComponentType<TextInputProps>}
                              label="Address line 2"
                              name="mailingAddressLine2"
                              disabled={props.disabled || props.isRequestingPassportApi}
                              helpText="E.g. Apartment, suite, unit"
                            />
                          )}
                        </Flex.Item>
                        <Flex basis="100%" gap={4}>
                          <Flex.Item basis={{ xs: 'calc(50% - 8px)' }}>
                            {isExtractingData && isAiProcessingEnabled ? (
                              <LoadingField label="Province/state/region" required />
                            ) : (
                              <Field
                                as={TextInput as ComponentType<TextInputProps>}
                                label="Province/state/region"
                                name="mailingAddressProvince"
                                disabled={props.disabled || props.isRequestingPassportApi}
                                required={
                                  !props.disabled ? 'Province/state/region is required' : false
                                }
                              />
                            )}
                          </Flex.Item>
                          <Flex.Item basis={{ xs: 'calc(50% - 8px)' }}>
                            {isExtractingData && isAiProcessingEnabled ? (
                              <LoadingField label="City/town" required />
                            ) : (
                              <Field
                                as={TextInput as ComponentType<TextInputProps>}
                                label="City/town"
                                name="mailingAddressCity"
                                disabled={props.disabled || props.isRequestingPassportApi}
                                required={!props.disabled ? 'City/town is required' : false}
                              />
                            )}
                          </Flex.Item>
                        </Flex>
                        <Flex.Item basis={{ xs: '100%', sm: 'calc(50% - 8px)' }}>
                          {isExtractingData && isAiProcessingEnabled ? (
                            <LoadingField label="Postal/zip code" />
                          ) : (
                            <Field
                              as={TextInput as ComponentType<TextInputProps>}
                              label="Postal/zip code"
                              name="mailingAddressPostalCode"
                              disabled={props.disabled || props.isRequestingPassportApi}
                            />
                          )}
                        </Flex.Item>
                      </Flex>
                    </>
                  ) : null}
                </Flex>
              </Flex>
              
              <Divider />

              <StatusSection 
                // @ts-ignore - Field from createForm has complex generics that make it difficult to type when passed as prop
                Field={Field}
                application={props.application}
                destinationCountryName={props.destinationCountryName}
                disabled={props.disabled}
                isRequestingPassportApi={props.isRequestingPassportApi}
                immigrationFiles={immigrationFiles}
                immigrationStatus={immigrationStatus}
                setFieldValues={setFieldValues}
              />
            </>
          ) : null}
        </>
      ) : null}
    </>
  )
}